<template>
  <tile class="opc-healthcare">
    <foldable
      v-model="isExpanded"
      :title="title"
      :editLabel="$t('billing.common.view')"
      :editAriaLabel="$t('billing.common.view')"
      :arrow="agreementAccepted"
      isRemoveWrapAriaLabelledby
    >
      <template #head>
        <div class="opc-zm-base-card__head">
          <div class="opc-zm-base-card__head-left">
            <img class="opc-zm-base-card__logo" :src="logo" :alt="title" tabindex="-1" />
          </div>
          <div class="opc-zm-base-card__head-right">
            <h1 class="opc-zm-base-card__name">
              <span>{{ title }}</span>
              <i v-if="agreementAccepted" class="zm-icon-success main-icon" aria-hidden="true" />
            </h1>
            <div v-if="!isExpanded && foldedDesc" class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset">
              {{ foldedDesc }}
            </div>
          </div>
        </div>
      </template>

      <div class="opc-healthcare__main">
        <div class="opc-healthcare__main__content">{{ $t('billing.healthcare.desc') }}</div>
        <zm-select
          data-testid="checkout-select-144"
          v-model="businessCountry"
          class="opc-healthcare__main__business-country-select"
          size="xLarge"
          :inline-label="`${$t('billing.healthcare.business_country_select.label')} *`"
          :label="$t('billing.healthcare.business_country_select.label')"
          aria-required="true"
          @change="onBusinessCountrySelectChange"
        >
          <zm-option data-testid="checkout-option-145" v-for="item in businessCountryList" :key="item.value" :label="item.label" :value="item.value" />
        </zm-select>
        <zm-alert
          v-if="notEligibleCountry"
          class="opc-healthcare__custom-alert opc-healthcare__main__not-eligible-tip"
          type="info"
          :closable="false"
        >
          <i class="zm-icon-info opc-healthcare__main__not-eligible-tip__icon" aria-hidden="true" />
          <div
            data-testid="checkout-div-146"
            v-html="$t('billing.healthcare.not_eligible_tip', [hostPlanName])"
            class="opc-healthcare__main__not-eligible-tip__content"
            @click="onNotEligibleCountryTipClick"
          />
        </zm-alert>
        <template v-else>
          <zm-button
            data-testid="checkout-button-147"
            class="opc-healthcare__main__read-agreement"
            type="primary"
            size="large"
            round
            @click="onReadAgreementClick"
          >
            {{ $t('billing.healthcare.read_agreement') }}
          </zm-button>
          <div v-if="agreementAccepted" class="opc-healthcare__main__accepted-desc">
            <span>{{ agreementAcceptedDesc }}</span>
            <i class="zm-icon-success opc-healthcare__main__accepted-desc__icon" aria-hidden="true" />
          </div>
        </template>
      </div>

      <AgreementDialog
        :visible.sync="agreementDialogShow"
        :accepted.sync="agreementAccepted"
        :businessCountry="businessCountry"
      />
    </foldable>
  </tile>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import logo from '@/assets/plans/healthcare.svg'
import Foldable from '@/components/foldable.vue'
import Tile from '@/components/tile.vue'
import AgreementDialog from './agreement-dialog.vue'
import {
  BUSINESS_COUNTRY_VALUE_MAP,
  createBusinessCountryList,
  getAgreementAcceptedDesc,
  getBusinessCountryByIpLocation,
  notHealthcareEligibleCountry,
} from './helpers'
import { translateToEn } from '@/plugins/i18n'
import { ZOOM_MEETING_SUB_TYPES, getTitleNameI18nKey } from '../../common/utils'
import { INDUSTRIES } from '@/common/constants'
import { htmlTextToPlainText } from '@/utils'

function getCountryTrackingNameByBusinessCountry(businessCountry) {
  switch (businessCountry) {
    case BUSINESS_COUNTRY_VALUE_MAP.UnitedStates:
      return 'united states agreement (baa)'
    case BUSINESS_COUNTRY_VALUE_MAP.Canada:
      return 'canada agreement (phia)'
    default:
      return 'other'
  }
}

export default {
  name: 'Healthcare',

  components: {
    Tile,
    Foldable,
    AgreementDialog,
  },

  data() {
    const hostPlanNameI18nKey = getTitleNameI18nKey(ZOOM_MEETING_SUB_TYPES.PRO)

    return {
      isExpanded: true,
      logo,
      title: this.$t('billing.healthcare.title'),
      businessCountryList: createBusinessCountryList(this.$t.bind(this)),
      agreementDialogShow: false,
      hostPlanName: this.$t(hostPlanNameI18nKey),
      trackingData: {
        hostPlanName: translateToEn(hostPlanNameI18nKey),
        planName: translateToEn('billing.healthcare.title'),
      },
    }
  },

  computed: {
    ...mapGetters(['healthcareInfo', 'ipLocation']),
    agreementAccepted: {
      get({ healthcareInfo }) {
        return healthcareInfo.agreementAccepted
      },
      set(newValue) {
        this.updateHealthcare({ agreementAccepted: newValue })
      },
    },
    businessCountry: {
      get({ healthcareInfo, ipLocation }) {
        let value = healthcareInfo.businessCountry
        if (!value) {
          value = getBusinessCountryByIpLocation(ipLocation?.cn)
          this.updateHealthcare({ businessCountry: value })
        }
        return value
      },
      set(newValue) {
        this.updateHealthcare({ businessCountry: newValue })
      },
    },
    agreementAcceptedDesc() {
      if (!this.agreementAccepted) return ''
      return getAgreementAcceptedDesc(this.businessCountry, this.$t.bind(this))
    },
    foldedDesc() {
      return this.agreementAcceptedDesc
    },
    notEligibleCountry() {
      return notHealthcareEligibleCountry(this.businessCountry)
    },
  },

  watch: {
    isExpanded(val) {
      this.$tracking.buyInteraction(
        `${this.trackingData.planName} details`,
        `${this.trackingData.hostPlanName} ${INDUSTRIES.HEALTHCARE} - ${val ? 'expand' : 'collapse'}`,
      )
    },
    businessCountry: {
      immediate: true,
      handler(businessCountry) {
        this.$tracking.buyInteraction(
          `${this.trackingData.planName} details`,
          `${this.trackingData.hostPlanName} ${INDUSTRIES.HEALTHCARE} - ${getCountryTrackingNameByBusinessCountry(
            businessCountry,
          )}`,
        )
      },
    },
    notEligibleCountry: {
      immediate: true,
      handler(notEligibleCountry) {
        if (notEligibleCountry) {
          this.$tracking.buyInteraction(
            'banner - product level',
            `${this.trackingData.planName} - ${htmlTextToPlainText(
              translateToEn('billing.healthcare.not_eligible_tip', [this.trackingData.hostPlanName]),
            )}`,
          )
        }
      },
    },
  },

  methods: {
    ...mapMutations(['updateHealthcare']),
    onBusinessCountrySelectChange() {
      this.agreementAccepted = false
    },
    onReadAgreementClick() {
      this.agreementDialogShow = true

      this.$tracking.buyInteraction(
        `${this.trackingData.planName} details`,
        `${this.trackingData.hostPlanName} ${INDUSTRIES.HEALTHCARE} - read agreement`,
      )
    },
    onNotEligibleCountryTipClick({ target }) {
      if (target instanceof HTMLAnchorElement) {
        this.$tracking.buyInteraction(
          'banner - product level',
          `${this.trackingData.planName} - ${htmlTextToPlainText(
            translateToEn('billing.healthcare.not_eligible_tip', [this.trackingData.hostPlanName]),
          )} - contact sales`,
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.opc-healthcare {
  &__custom-alert {
    ::v-deep {
      .zm-alert {
        &__body {
          padding: 0;
        }
        &__description {
          display: flex;
          align-items: center;
        }
      }
    }
  }
  &__main {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #e0e0e6;
    &__content {
      font-size: 14px;
      line-height: 1.5;
      color: #00053d;
    }
    &__business-country-select {
      margin-top: 24px;
      width: 100%;
    }
    &__read-agreement {
      margin-top: 16px;
      min-width: auto !important;
      background-color: $theme-color;
    }
    &__accepted-desc {
      margin-top: 16px;
      font-size: 16px;
      font-weight: 500;
      line-height: 1.2;
      &__icon {
        margin-left: 8px;
        font-size: 16px;
        color: $theme-color;
      }
    }
    &__not-eligible-tip {
      margin-top: 16px;
      &__icon {
        flex-shrink: 0;
        font-size: 16px;
        color: #666487;
      }
      &__content {
        flex-grow: 1;
        margin-left: 16px;
        font-size: 14px;
        line-height: 1.5;
        color: #666487;
      }
    }
  }
}
</style>
